import { useMemo, useState } from 'react';
import SplitPaymentFields from './SplitPaymentFields';
import {
  defaultPaymentSplits,
  parsePaymentSplitsFromRow,
  sumPaymentSplits,
} from '../../../utils/splitPaymentUtils';
import { updateRemainingClearPayment } from '../../../services/receptionist/receptionistApi';
import { notifyPaymentsUpdated } from '../paymentStore';
import { usePanelToast } from '../../../context/PanelToastContext';
import { formatCurrency } from '../receptionistData';

function buildInitialForm(row) {
  const splits = parsePaymentSplitsFromRow(row);
  const paymentSplits = defaultPaymentSplits().map((blank) => {
    const hit = splits.find((s) => s.method === blank.method);
    return hit ? { method: blank.method, amount: String(hit.amount) } : blank;
  });
  const amount = Number(row?.amount) || sumPaymentSplits(splits) || 0;
  const note = String(row?.paymentNote || '')
    .replace(/^Remaining clear\s*·\s*[\d.]+\s*·\s*[^\s·]+\s*·?\s*/i, '')
    .trim();
  return {
    date: String(row?.date || row?.paidAt || '').slice(0, 10),
    amount: amount > 0 ? String(amount) : '',
    paymentSplits,
    note,
  };
}

/**
 * Edit a cleared Remaining Payment — amount, method, date.
 * Syncs the linked original sale so Cash/Card ledgers stay correct.
 */
export default function RemainingClearEditModal({ row, onClose, onSaved }) {
  const toast = usePanelToast();
  const [form, setForm] = useState(() => buildInitialForm(row));
  const [saving, setSaving] = useState(false);

  const paid = useMemo(
    () => sumPaymentSplits(form.paymentSplits),
    [form.paymentSplits],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    const paymentDbId = row?.rawId || row?.id;
    if (!paymentDbId) {
      toast.showError('Remaining clear record not found.');
      return;
    }
    if (!(paid > 0)) {
      toast.showError('Enter the cleared amount in Cash / Card / Bank / QR.');
      return;
    }

    try {
      setSaving(true);
      await updateRemainingClearPayment(paymentDbId, {
        amount: paid,
        paymentSplits: form.paymentSplits,
        date: form.date,
        paymentNote: form.note,
      });
      notifyPaymentsUpdated();
      toast.showUpdated('Remaining clear');
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.showError(err.message || 'Failed to update remaining clear');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="subscription-modal-overlay" onClick={onClose}>
      <div
        className="subscription-modal admin-form-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rc-edit-title"
      >
        <div className="subscription-modal-header">
          <h2 id="rc-edit-title" className="subscription-modal-title">
            Edit Remaining Clear
          </h2>
          <button type="button" className="subscription-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <form className="subscription-form" onSubmit={handleSubmit}>
          <p className="owner-placeholder-text" style={{ margin: '0 0 12px' }}>
            {row?.member || 'Member'}
            {row?.package ? ` · ${row.package}` : ''}
            {row?.invoiceId ? ` · Invoice ${row.invoiceId}` : ''}
          </p>

          <div className="membership-form-grid">
            <div className="owner-form-group">
              <label className="owner-form-label" htmlFor="rc-edit-date">Clear date</label>
              <input
                id="rc-edit-date"
                type="date"
                className="owner-form-input"
                value={form.date}
                onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))}
                required
              />
            </div>

            <div className="owner-form-group rec-ancillary-sale-full">
              <SplitPaymentFields
                idPrefix="rc-edit"
                splits={form.paymentSplits}
                onChange={(paymentSplits) => setForm((prev) => ({ ...prev, paymentSplits }))}
              />
            </div>

            <div className="owner-form-group rec-ancillary-sale-full">
              <p className="owner-placeholder-text" style={{ margin: 0, fontSize: 13 }}>
                Amount cleared: <strong>{formatCurrency(paid)}</strong>
                {' '}(change Cash → Card here if the method was wrong)
              </p>
            </div>

            <div className="owner-form-group membership-form-full">
              <label className="owner-form-label" htmlFor="rc-edit-note">Note (optional)</label>
              <input
                id="rc-edit-note"
                type="text"
                className="owner-form-input"
                value={form.note}
                onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))}
                placeholder="Optional note"
              />
            </div>
          </div>

          <div className="subscription-form-actions">
            <button type="button" className="owner-btn owner-btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="owner-btn owner-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
